/**
 * 阶段 3：核心攻防战斗与队友自动救援系统 (Combat & Rescue System)
 * 职责：
 * 1. 压制/翻盘/防守定点斩半：优势方合 n(>=64) 斩 n/2，劣势方合 n(>=64) 斩 n，无对应块则完美护盾格挡
 * 2. 非对称对抗平衡：少人方单次合成可同时向最多 ceil(敌人数 / 我人数) 个敌盘分裂横扫
 * 3. 抛物线弹道路由（支持参战与观战视角下主棋盘与小棋盘之间的精确弹道瞄准）与落点刀光撕裂结算
 * 4. 队友 5 秒无操作自动进入 SOS 待救援状态，存活队友得分达标后自动抛射 +2 空投救援包
 */

import type {
  AttackPacket,
  PKGamePacket,
  SeatOccupant,
} from "../../shared/protocol";
import {
  getRandomEmptySpot,
  isBoardStuck,
  evaluateDangerState,
  Tile,
} from "./boardEngine";
import { getDodgeChanceByGrade } from "./dmcRankManager";
import type { SoundEngine } from "./soundEngine";
import type { P2PChannel } from "../network/p2pChannel";
import type { LocalTileRenderer } from "../ui/pkRenderer";
import type { BattleEffects } from "../ui/battleEffects";
import type { ArenaDomRefs } from "../ui/domRefs";
import type { ArenaRuntimeState, ParticipantRuntime } from "../state/arenaState";
import type { BattlefieldView } from "./battlefieldView";

export class CombatSystem {
  private inFlightEjectCounts = new Map<string, number>();
  private activeEjectFlightsByTeam: Record<"red" | "blue", number> = {
    red: 0,
    blue: 0,
  };
  private pendingLaunchTimers = new Map<string, number>();

  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly state: ArenaRuntimeState,
    private readonly p2p: P2PChannel,
    private readonly sounds: SoundEngine,
    private readonly localRenderer: LocalTileRenderer,
    private readonly effects: BattleEffects,
    private readonly view: BattlefieldView,
    private readonly callbacks: {
      onFlushPendingMove: () => void;
      onBroadcastBoardState: () => void;
      onHandleIncomingPacket: (packet: PKGamePacket) => void;
      onEnterLocalStuckPendingEject: () => void;
      onConsumeLocalPendingEjectTile: () => Tile | null;
      onEvaluateTeamStuckConditions: () => void;
    }
  ) {}

  public resetEjectTracking() {
    for (const timer of this.pendingLaunchTimers.values()) {
      clearTimeout(timer);
    }
    this.pendingLaunchTimers.clear();
    this.inFlightEjectCounts.clear();
    this.activeEjectFlightsByTeam.red = 0;
    this.activeEjectFlightsByTeam.blue = 0;
  }

  /**
   * 全员 AOE 定点斩半机制：
   * - 不论对称或非对称，合出 n(>=64) 时向敌方全体存活目标同时发射光球
   * - 对每个受击目标单独比较分数：我方个人分 >= 目标个人分时触发压制砍 n/2；我方个人分 < 目标个人分时触发暴击砍 n
   */
  public triggerSniperAttack(mergedVal: number) {
    if (this.state.myTeam !== "red" && this.state.myTeam !== "blue") return;
    const enemyTeam: "red" | "blue" = this.state.myTeam === "red" ? "blue" : "red";

    this.dom.elLocalBoardWrapper.classList.add("board-overcharge-aura");
    setTimeout(() => {
      this.dom.elLocalBoardWrapper.classList.remove("board-overcharge-aura");
      this.state.attackGauge = 0;
      this.view.updateLocalStatsUI();
    }, 900);

    const selectedTargets = Array.from(this.state.participants.values()).filter(
      (p) => p.team === enemyTeam && !p.isStuck
    );
    if (selectedTargets.length === 0) return;

    const myScore = this.state.localScore;
    let critCount = 0;

    this.sounds.playChargeUp();
    this.sounds.playLaunchOrb();

    for (const target of selectedTargets) {
      const targetScore = this.state.getParticipantScore(target.id);
      const isUnderdogCrit = this.state.isUnderdogCritAgainst(myScore, targetScore);
      if (isUnderdogCrit) critCount++;

      const targetVal = isUnderdogCrit
        ? mergedVal
        : Math.max(32, Math.floor(mergedVal / 2));
      const orbLabel = `${targetVal}÷2`;

      const targetGrade = target.styleGrade || "D";
      const dodgeChance = getDodgeChanceByGrade(targetGrade);
      const isMissed = Math.random() < dodgeChance;

      const attack: AttackPacket = {
        id: `atk_${this.state.myPeerId}_${target.id}_${Date.now()}`,
        type: "CORROSION_DRAIN",
        senderId: this.state.myPeerId,
        senderName: this.state.myName,
        senderTeam: this.state.myTeam,
        targetId: target.id,
        power: targetVal,
        targetVal,
        isMissed,
        dodgeGrade: targetGrade,
        description: orbLabel,
        timestamp: Date.now(),
      };

      this.p2p.sendPacket({ type: "attack", attack });
      this.animateAttackBetweenParticipants(attack);
    }

    if (selectedTargets.length === 1) {
      const onlyTarget = selectedTargets[0];
      const isCrit = this.state.isUnderdogCritAgainst(
        myScore,
        this.state.getParticipantScore(onlyTarget.id)
      );
      const val = isCrit ? mergedVal : Math.max(32, Math.floor(mergedVal / 2));
      this.view.logCombat(
        isCrit ? `翻盘暴击 · ${val}÷2` : `压制斩击 · ${val}÷2`
      );
    } else {
      this.view.logCombat(
        `AOE 齐射 ×${selectedTargets.length}${critCount > 0 ? ` (含 ${critCount} 发暴击 ${mergedVal}÷2)` : ` (压制 ${Math.max(32, Math.floor(mergedVal / 2))}÷2)`}`
      );
    }
  }

  /**
   * 渲染任意选手之间的抛物线斩击动画并在落点结算命中/格挡
   */
  public animateAttackBetweenParticipants(attack: AttackPacket) {
    const targetVal = attack.targetVal || attack.power || 32;
    const orbLabel = `${targetVal}÷2`;
    const mainBoardId = this.state.getMainBoardPlayerId();
    const viewTeam = this.state.getViewTeam();

    const isFromMain = attack.senderId === mainBoardId;
    const isToMain = attack.targetId === mainBoardId;
    const isToLocalPlayer =
      attack.targetId === this.state.myPeerId && this.state.myTeam !== "spectator";

    const senderRuntime = attack.senderId
      ? this.state.participants.get(attack.senderId)
      : undefined;
    const targetRuntime = attack.targetId
      ? this.state.participants.get(attack.targetId)
      : undefined;

    const fromEl = isFromMain
      ? this.dom.elLocalBoardWrapper
      : senderRuntime?.wrapperEl || this.dom.elRivalMirrorWrapper;
    const toEl = isToMain
      ? this.dom.elLocalBoardWrapper
      : targetRuntime?.wrapperEl || this.dom.elRivalMirrorWrapper;

    this.effects.launchBallisticProjectile(
      fromEl,
      toEl,
      {
        isOutbound: isFromMain || senderRuntime?.team === viewTeam,
        label: orbLabel,
      },
      () => {
        if (this.state.hasFinished) return;
        if (isToLocalPlayer) {
          if (this.state.isBoardLocked) return;
          this.applyAttackEffectToLocal(attack);
          return;
        }
        if (targetRuntime?.isStuck) return;

        // 若目标是房主托管的 AI 机器人
        if (targetRuntime?.isBot && this.state.isRoomHost()) {
          const bot = this.state.activeBots.get(targetRuntime.id);
          if (bot) {
            const res = bot.receiveAttack(attack);
            const drainedVal = Math.max(2, Math.floor(targetVal / 2));
            if (res.hit) {
              if (senderRuntime) {
                senderRuntime.slashHits++;
                senderRuntime.scoreDrained += drainedVal;
              }
              if (isFromMain || this.state.myTeam === "spectator") {
                this.state.slashHits++;
                this.state.scoreDrained += drainedVal;
              }
              this.sounds.playSlashCut();
              this.effects.shakeBoard(toEl);
              this.effects.spawnImpactBadgeOnElement(toEl, res.text, isFromMain);
              this.view.updateTeamScoresHUD();
            } else if (res.missed) {
              targetRuntime.shieldBlocks++;
              if (isToMain || this.state.myTeam === "spectator") {
                this.state.shieldBlocks++;
                this.view.updateTeamScoresHUD();
              }
              this.sounds.playClutch();
              this.effects.playDodgeMissEffect(toEl, res.text);
              this.view.logCombat(`⚡ 残影闪避 · ${targetRuntime.name} ${res.text}`);
            } else {
              targetRuntime.shieldBlocks++;
              if (isToMain || this.state.myTeam === "spectator") {
                this.state.shieldBlocks++;
                this.view.updateTeamScoresHUD();
              }
              this.sounds.playShieldBlock();
              this.effects.playShieldBlockEffect(toEl, res.text);
            }
            return;
          }
        }

        // 远端真人或非本机托管目标的视觉命中/MISS/格挡反馈
        const hasTarget = targetRuntime?.tiles.some((t) => t.val === targetVal);
        const drainedVal = Math.max(2, Math.floor(targetVal / 2));
        if (hasTarget) {
          if (attack.isMissed) {
            if (targetRuntime) targetRuntime.shieldBlocks++;
            if (isToMain || this.state.myTeam === "spectator") {
              this.state.shieldBlocks++;
            }
            const grade = attack.dodgeGrade || targetRuntime?.styleGrade || "S";
            this.sounds.playClutch();
            this.effects.playDodgeMissEffect(toEl, `⚡ ${grade}级闪避 MISS!`);
            this.view.logCombat(`⚡ 残影闪避 · ${grade}级 MISS (${targetVal}÷2)`);
          } else {
            if (senderRuntime) {
              senderRuntime.slashHits++;
              senderRuntime.scoreDrained += drainedVal;
            }
            if (isFromMain || this.state.myTeam === "spectator") {
              this.state.slashHits++;
              this.state.scoreDrained += drainedVal;
            }
            this.sounds.playSlashCut();
            this.effects.shakeBoard(toEl);
            this.effects.spawnImpactBadgeOnElement(
              toEl,
              `${targetVal} ➔ ${drainedVal}`,
              isFromMain
            );
          }
        } else {
          if (targetRuntime) targetRuntime.shieldBlocks++;
          if (isToMain || this.state.myTeam === "spectator") {
            this.state.shieldBlocks++;
          }
          this.sounds.playShieldBlock();
          this.effects.playShieldBlockEffect(toEl, `${targetVal}÷2 格挡!`);
        }
        this.view.updateTeamScoresHUD();
      }
    );
  }

  /**
   * 结算命中本地棋盘的定点斩半或护盾格挡（若本机已封盘定格则不再改变任何状态）
   */
  public applyAttackEffectToLocal(attack: AttackPacket) {
    if (this.state.isBoardLocked || this.state.hasFinished) return;
    this.callbacks.onFlushPendingMove();
    const targetVal = attack.targetVal || attack.power || 32;
    const senderRuntime = attack.senderId
      ? this.state.participants.get(attack.senderId)
      : undefined;
    const myRuntime = this.state.participants.get(this.state.myPeerId);

    const candidates = this.state.localTiles
      .filter((t) => t.val === targetVal)
      .sort((a, b) => b.r * 4 - b.c - (a.r * 4 - a.c));
    const target = candidates[0];

    if (target) {
      if (attack.isMissed) {
        this.state.shieldBlocks++;
        if (myRuntime) myRuntime.shieldBlocks++;
        this.view.updateTeamScoresHUD();
        const grade = attack.dodgeGrade || "S";
        const missText = `⚡ ${grade}级闪避 MISS!`;
        this.sounds.playClutch();
        this.effects.playDodgeMissEffect(this.dom.elLocalBoardWrapper, missText);
        this.view.logCombat(`⚡ 华丽闪避 · ${grade}级残影避开了 ${targetVal}÷2！`);
        return;
      }

      const oldVal = target.val;
      const newVal = Math.max(2, Math.floor(oldVal / 2));
      const drained = oldVal - newVal;

      if (senderRuntime) {
        senderRuntime.slashHits++;
        senderRuntime.scoreDrained += drained;
      }

      this.state.localScore = Math.max(0, this.state.localScore - drained);
      this.state.localMaxTile = this.state.localTiles.reduce(
        (m, t) => Math.max(m, t.id === target.id ? newVal : t.val),
        2
      );

      this.localRenderer.playSlashAndSplitEffect(target, oldVal, newVal);

      const badgeText = `${oldVal} ➔ ${newVal}`;
      this.sounds.playSlashCut();
      this.effects.shakeBoard(this.dom.elLocalBoardWrapper);
      this.effects.spawnImpactBadgeOnElement(this.dom.elLocalBoardWrapper, badgeText, false);
      this.view.updateLocalStatsUI();
      this.view.logCombat(`受击 · ${badgeText}`);
      if (isBoardStuck(this.state.localTiles)) {
        this.callbacks.onEnterLocalStuckPendingEject();
      } else {
        this.callbacks.onBroadcastBoardState();
      }
      return;
    }

    this.state.shieldBlocks++;
    if (myRuntime) myRuntime.shieldBlocks++;
    this.view.updateTeamScoresHUD();
    const blockText = `${targetVal}÷2 格挡!`;
    this.sounds.playShieldBlock();
    this.effects.playShieldBlockEffect(this.dom.elLocalBoardWrapper, blockText);
    this.view.logCombat(`防御成功 · ${blockText}`);
  }

  /**
   * 房主处理本地 AI 机器人产生的同步包、攻击包与终局包，并向全房广播
   */
  public handleBotGeneratedPacket(botOcc: SeatOccupant, packet: PKGamePacket) {
    if (!this.state.isMatchRunning || this.state.hasFinished) return;

    if (packet.type === "sync_board") {
      this.callbacks.onHandleIncomingPacket(packet);
      this.p2p.sendPacket(packet);
      return;
    }

    if (packet.type === "attack") {
      const senderTeam = botOcc.team;
      const enemyTeam: "red" | "blue" = senderTeam === "red" ? "blue" : "red";
      const targets = Array.from(this.state.participants.values()).filter(
        (p) => p.team === enemyTeam && !p.isStuck
      );
      if (targets.length === 0) return;

      const botScore = this.state.getParticipantScore(botOcc.id);
      const mergedApprox = packet.attack.power || 64;

      for (const target of targets) {
        const targetScore = this.state.getParticipantScore(target.id);
        const isUnderdogCrit = this.state.isUnderdogCritAgainst(botScore, targetScore);
        const targetVal = isUnderdogCrit
          ? mergedApprox
          : Math.max(32, Math.floor(mergedApprox / 2));

        const targetGrade = target.styleGrade || "D";
        const dodgeChance = getDodgeChanceByGrade(targetGrade);
        const isMissed = Math.random() < dodgeChance;

        const outAtk: AttackPacket = {
          ...packet.attack,
          id: `atk_${botOcc.id}_${target.id}_${Date.now()}`,
          senderId: botOcc.id,
          senderName: botOcc.name,
          senderTeam,
          targetId: target.id,
          targetVal,
          power: targetVal,
          isMissed,
          dodgeGrade: targetGrade,
          description: `${targetVal}÷2`,
        };
        const atkPacket: PKGamePacket = { type: "attack", attack: outAtk };
        this.callbacks.onHandleIncomingPacket(atkPacket);
        this.p2p.sendPacket(atkPacket);
      }
      return;
    }

    if (packet.type === "game_over") {
      this.callbacks.onHandleIncomingPacket(packet);
      this.p2p.sendPacket(packet);
    }
  }

  /**
   * 获取指定选手的实时封盘状态与占用格数（含正在飞向该选手的待落地方块数）
   */
  public getParticipantEffectiveOccupancy(p: ParticipantRuntime): {
    isStuck: boolean;
    hasPendingEject: boolean;
    tileCount: number;
    effectiveCount: number;
  } {
    const inFlightTo = this.inFlightEjectCounts.get(p.id) || 0;
    if (p.id === this.state.myPeerId && this.state.myTeam !== "spectator") {
      const tileCount = this.state.localTiles.length;
      const hasPendingEject =
        this.state.isBoardLocked &&
        this.state.localTiles.some((t) => Boolean(t.isPendingEject));
      return {
        isStuck: this.state.isBoardLocked,
        hasPendingEject,
        tileCount,
        effectiveCount: tileCount + inFlightTo,
      };
    }
    if (p.isBot && this.state.isRoomHost()) {
      const bot = this.state.activeBots.get(p.id);
      if (bot) {
        const tileCount = bot.getTiles().length;
        return {
          isStuck: bot.isStuck(),
          hasPendingEject: bot.hasPendingEjectTile(),
          tileCount,
          effectiveCount: tileCount + inFlightTo,
        };
      }
    }
    const tileCount = p.tiles.length;
    return {
      isStuck: p.isStuck,
      hasPendingEject: p.isStuck && p.tiles.some((t) => Boolean(t.isPendingEject)),
      tileCount,
      effectiveCount: tileCount + inFlightTo,
    };
  }

  /**
   * 查找指定阵营中所有存活且当前有空位 (effectiveCount < 16) 的队友列表（用于群发待发射方块）
   */
  public findTeammatesWithEmptySpot(
    fromId: string,
    team: "red" | "blue"
  ): ParticipantRuntime[] {
    const candidates: Array<{ ally: ParticipantRuntime; effectiveCount: number }> = [];
    for (const p of this.state.participants.values()) {
      if (p.team !== team || p.id === fromId) continue;
      const occ = this.getParticipantEffectiveOccupancy(p);
      if (!occ.isStuck && occ.effectiveCount < 16) {
        candidates.push({ ally: p, effectiveCount: occ.effectiveCount });
      }
    }
    candidates.sort((a, b) => a.effectiveCount - b.effectiveCount);
    return candidates.map((c) => c.ally);
  }

  /**
   * 查找指定阵营中存活且当前有空位 (effectiveCount < 16) 的单个最优队友
   */
  public findTeammateWithEmptySpot(
    fromId: string,
    team: "red" | "blue"
  ): ParticipantRuntime | undefined {
    return this.findTeammatesWithEmptySpot(fromId, team)[0];
  }

  /**
   * 判断指定阵营是否仍有正在飞行中的抛射块，或存在「持有待发射方块的封盘成员 + 有空位存活队友」的可流转状态
   */
  public hasActiveOrTransferableEject(team: "red" | "blue"): boolean {
    if (this.activeEjectFlightsByTeam[team] > 0) return true;

    const members = Array.from(this.state.participants.values()).filter(
      (p) => p.team === team
    );
    if (members.length <= 1) return false;

    const hasPendingStuckMember = members.some(
      (p) => this.getParticipantEffectiveOccupancy(p).hasPendingEject
    );
    const hasOpenTeammate = members.some((p) => {
      const occ = this.getParticipantEffectiveOccupancy(p);
      return !occ.isStuck && occ.effectiveCount < 16;
    });

    return hasPendingStuckMember && hasOpenTeammate;
  }

  /**
   * 异步调度本阵营中处于「待发射 (isPendingEject)」状态的方块向所有有空位的队友群发（0ms 零延迟异步宏任务）
   */
  public scheduleOrFlushPendingEjects(team: "red" | "blue") {
    if (!this.state.isMatchRunning || this.state.hasFinished) return;

    // 1. 检查本机人类玩家是否有待发射方块
    if (
      this.state.myTeam === team &&
      this.state.isBoardLocked &&
      this.state.localTiles.some((t) => Boolean(t.isPendingEject))
    ) {
      const myId = this.state.myPeerId;
      if (!this.pendingLaunchTimers.has(myId)) {
        const timer = window.setTimeout(() => {
          this.pendingLaunchTimers.delete(myId);
          this.tryFlushSingleSenderPendingEject(myId, team);
        }, 0);
        this.pendingLaunchTimers.set(myId, timer);
      }
    }

    // 2. 若本机为房主，检查本阵营 AI 机器人是否有待发射方块
    if (this.state.isRoomHost()) {
      for (const bot of this.state.activeBots.values()) {
        if (bot.team === team && bot.hasPendingEjectTile()) {
          const botId = bot.botId;
          if (!this.pendingLaunchTimers.has(botId)) {
            const timer = window.setTimeout(() => {
              this.pendingLaunchTimers.delete(botId);
              this.tryFlushSingleSenderPendingEject(botId, team);
            }, 0);
            this.pendingLaunchTimers.set(botId, timer);
          }
        }
      }
    }
  }

  private tryFlushSingleSenderPendingEject(senderId: string, team: "red" | "blue") {
    if (!this.state.isMatchRunning || this.state.hasFinished) return;

    const targetAllies = this.findTeammatesWithEmptySpot(senderId, team);
    if (targetAllies.length === 0) {
      this.callbacks.onEvaluateTeamStuckConditions();
      return;
    }

    const now = Date.now();

    // 本机玩家群发给所有有空位的队友
    if (senderId === this.state.myPeerId && this.state.myTeam === team) {
      if (!this.state.isBoardLocked) return;
      const ejectedTile = this.callbacks.onConsumeLocalPendingEjectTile();
      if (!ejectedTile) return;

      for (const targetAlly of targetAllies) {
        const ejectPkt: PKGamePacket = {
          type: "stuck_tile_eject",
          id: `eject_${senderId}_${targetAlly.id}_${now}`,
          fromId: senderId,
          fromName: this.state.myName,
          toId: targetAlly.id,
          toName: targetAlly.name,
          team,
          val: ejectedTile.val,
        };
        this.callbacks.onHandleIncomingPacket(ejectPkt);
        this.p2p.sendPacket(ejectPkt);
      }
      return;
    }

    // 房主托管的 AI 机器人群发给所有有空位的队友
    if (this.state.isRoomHost()) {
      const bot = this.state.activeBots.get(senderId);
      if (bot && bot.team === team && bot.hasPendingEjectTile()) {
        const ejectedTile = bot.consumePendingEjectTile();
        if (!ejectedTile) return;

        for (const targetAlly of targetAllies) {
          const ejectPkt: PKGamePacket = {
            type: "stuck_tile_eject",
            id: `eject_${bot.botId}_${targetAlly.id}_${now}`,
            fromId: bot.botId,
            fromName: bot.botName,
            toId: targetAlly.id,
            toName: targetAlly.name,
            team,
            val: ejectedTile.val,
          };
          this.callbacks.onHandleIncomingPacket(ejectPkt);
          this.p2p.sendPacket(ejectPkt);
        }
      }
    }
  }

  /**
   * 播放封盘「待发射」最后原生方块向有空位队友抛射的飞行轨迹，并在落点结算：
   * - 落入目标队友随机空位的方块标记为 isTransferred: true（外来发射块，非目标队友原生块）
   * - 若接收该方块后导致目标队友封盘，则根据定理：目标队友棋盘上最后生成的原生方块状态变为「待发射」
   */
  public animateStuckTileEject(pkt: {
    id: string;
    fromId: string;
    fromName: string;
    toId: string;
    toName: string;
    team: "red" | "blue";
    val: number;
  }) {
    const mainBoardId = this.state.getMainBoardPlayerId();
    const fromRuntime = this.state.participants.get(pkt.fromId);
    const toRuntime = this.state.participants.get(pkt.toId);
    if (fromRuntime) {
      fromRuntime.ejectCount++;
    }

    const fromEl =
      pkt.fromId === mainBoardId
        ? this.dom.elLocalBoardWrapper
        : fromRuntime?.wrapperEl || this.dom.elAlliesMiniContainer;
    const toEl =
      pkt.toId === mainBoardId
        ? this.dom.elLocalBoardWrapper
        : toRuntime?.wrapperEl || this.dom.elAlliesMiniContainer;

    this.inFlightEjectCounts.set(
      pkt.toId,
      (this.inFlightEjectCounts.get(pkt.toId) || 0) + 1
    );
    this.activeEjectFlightsByTeam[pkt.team] =
      (this.activeEjectFlightsByTeam[pkt.team] || 0) + 1;

    this.sounds.playLaunchOrb();
    this.view.logCombat(
      `封盘转嫁 · ${pkt.fromName} ➔ ${pkt.toName} (+${pkt.val})`
    );

    this.effects.launchBallisticProjectile(
      fromEl,
      toEl,
      {
        isOutbound: true,
        isRescue: true,
        label: `+${pkt.val}`,
      },
      () => {
        const prevCnt = this.inFlightEjectCounts.get(pkt.toId) || 0;
        if (prevCnt <= 1) {
          this.inFlightEjectCounts.delete(pkt.toId);
        } else {
          this.inFlightEjectCounts.set(pkt.toId, prevCnt - 1);
        }
        this.activeEjectFlightsByTeam[pkt.team] = Math.max(
          0,
          (this.activeEjectFlightsByTeam[pkt.team] || 0) - 1
        );

        if (this.state.hasFinished || !this.state.isMatchRunning) return;

        // 若接收方在飞行途中刚刚封盘且其待发射方块尚在 220ms 蓄能前摇中，落地前先立即触发其发射以腾出第 16 格空位！
        if (this.pendingLaunchTimers.has(pkt.toId)) {
          const pendingTimer = this.pendingLaunchTimers.get(pkt.toId)!;
          clearTimeout(pendingTimer);
          this.pendingLaunchTimers.delete(pkt.toId);
          this.tryFlushSingleSenderPendingEject(pkt.toId, pkt.team);
        }

        // 1. 若接收目标是本机人类玩家
        if (pkt.toId === this.state.myPeerId && this.state.myTeam !== "spectator") {
          this.callbacks.onFlushPendingMove();
          const spot = !this.state.isBoardLocked
            ? getRandomEmptySpot(this.state.localTiles)
            : null;
          if (!spot) {
            // 飞行途中接收方已滑满 16 格（未封盘满格，或全队均已满格无处可射）➔ 触发满盘 MISS！
            this.sounds.playClutch();
            this.effects.playDodgeMissEffect(toEl, "⚡ 满盘 MISS!");
            this.view.logCombat(
              `⚡ 满盘 MISS · ${pkt.toName} 棋盘已满，弹开了 +${pkt.val}`
            );
            this.scheduleOrFlushPendingEjects(pkt.team);
            this.callbacks.onEvaluateTeamStuckConditions();
            return;
          }

          this.sounds.playShieldBlock();
          this.effects.spawnImpactBadgeOnElement(toEl, `+${pkt.val} 承接!`, true);
          const tile: Tile = {
            id: this.state.nextTileId++,
            r: spot.r,
            c: spot.c,
            val: pkt.val,
            isNew: true,
            isTransferred: true, // 标记为外来抛射方块
          };
          this.state.localTiles.push(tile);
          if (pkt.val > this.state.localMaxTile) {
            this.state.localMaxTile = pkt.val;
          }
          this.localRenderer.mountTile(tile, "tile-dropped-pulse");
          const danger = evaluateDangerState(this.state.localTiles);
          this.dom.elLocalBoardWrapper.classList.toggle("in-danger", danger.isCritical);

          // 接收后若触发封盘，立刻生成待发射方块并在队友有空位时继续发射
          if (isBoardStuck(this.state.localTiles)) {
            this.callbacks.onEnterLocalStuckPendingEject();
          } else {
            this.view.updateLocalStatsUI();
            this.callbacks.onBroadcastBoardState();
            this.scheduleOrFlushPendingEjects(pkt.team);
            this.callbacks.onEvaluateTeamStuckConditions();
          }
          return;
        }

        // 2. 若接收目标是房主托管的 AI 机器人
        if (toRuntime?.isBot && this.state.isRoomHost()) {
          const bot = this.state.activeBots.get(toRuntime.id);
          const accepted = bot ? bot.receiveEjectedTile(pkt.val) : false;
          if (accepted) {
            this.sounds.playShieldBlock();
            this.effects.spawnImpactBadgeOnElement(toEl, `+${pkt.val} 承接!`, true);
          } else {
            this.sounds.playClutch();
            this.effects.playDodgeMissEffect(toEl, "⚡ 满盘 MISS!");
            this.view.logCombat(
              `⚡ 满盘 MISS · ${pkt.toName} 棋盘已满，弹开了 +${pkt.val}`
            );
          }
          this.scheduleOrFlushPendingEjects(pkt.team);
          this.callbacks.onEvaluateTeamStuckConditions();
          return;
        }

        // 3. 远端真人或观战视角反馈
        if (toRuntime && (toRuntime.isStuck || toRuntime.tiles.length >= 16)) {
          this.sounds.playClutch();
          this.effects.playDodgeMissEffect(toEl, "⚡ 满盘 MISS!");
          this.view.logCombat(
            `⚡ 满盘 MISS · ${pkt.toName} 棋盘已满，弹开了 +${pkt.val}`
          );
        } else {
          this.sounds.playShieldBlock();
          this.effects.spawnImpactBadgeOnElement(toEl, `+${pkt.val} 承接!`, true);
        }

        this.scheduleOrFlushPendingEjects(pkt.team);
        this.callbacks.onEvaluateTeamStuckConditions();
      }
    );
  }
}

